// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Avatar, Button, CircularProgress,
  Paper, IconButton, Alert, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Divider
} from '@mui/material';
import {
  ArrowBack, AccessTime, CalendarMonth,
  ContentCut, Refresh, Close, Edit, Person,
  PhotoCamera, Lock, AccountCircle, Star, StarBorder, CameraAlt
} from '@mui/icons-material';
import { format, parseISO, isBefore, startOfDay, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';
import { MyAccountV2 } from './pilot/MyAccountV2';
import { MyAccountSkeleton } from './pilot/MyAccountSkeleton';
import { sortUpcomingAsc, sortPastDesc } from './pilot/bookingSort';

const API = process.env.REACT_APP_API_URL || 'https://app.agendali.com.br/api';
const getSlug = () => {
  const params = new URLSearchParams(window.location.search);
  const urlSlug = params.get('s');
  if (urlSlug) return urlSlug;
  const path = window.location.pathname;
  const m = path.match(/\/book\/([^/]+)/);
  if (m) return m[1];
  return localStorage.getItem('cust_slug') || 'demo';
};
const today = startOfDay(new Date());

function loadCustomer() {
  try { return JSON.parse(localStorage.getItem('cust_user') || 'null'); } catch { return null; }
}
function saveCustomer(u: any) {
  localStorage.setItem('cust_user', JSON.stringify(u));
}

function cleanPhone(value: any) {
  return String(value || '').replace(/\D/g, '');
}

function getUrlPhone() {
  const params = new URLSearchParams(window.location.search);
  return cleanPhone(params.get('phone') || params.get('whatsapp'));
}

function getRemainingSubscriptionQty(item: any) {
  return Math.max(0, Number(item?.quantity_total || 0) - Number(item?.quantity_used || 0));
}

function getSubscriptionDaysLeft(endDate?: string) {
  if (!endDate) return 0;
  const todayBase = startOfDay(new Date());
  const end = startOfDay(parseISO(`${endDate}T12:00:00`));
  if (isNaN(end.getTime())) return 0;
  return Math.max(0, Math.ceil((end.getTime() - todayBase.getTime()) / 86400000) + 1);
}

const STATUS_MAP: any = {
  confirmed: { label: 'Confirmado', color: '#2e7d32', bg: '#e8f5e9' },
  pending:   { label: 'Pendente',   color: '#e65100', bg: '#fff3e0' },
  completed: { label: 'Concluído',  color: '#1565c0', bg: '#e3f2fd' },
  cancelled: { label: 'Cancelado',  color: '#b71c1c', bg: '#ffebee' },
  no_show:   { label: 'Não veio',   color: '#555',    bg: '#f5f5f5' },
};

export default function MyBookingsPage() {
  // Bootstrap: se viemos de um redirect de booking, token e slug estão na URL
  const [tokenExpired, setTokenExpired] = useState(false);
  const [custUser, setCustUser] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('t');
    const urlSlug = params.get('s');
    const urlPhone = getUrlPhone();
    if (urlSlug) localStorage.setItem('cust_slug', urlSlug);
    if (urlPhone) localStorage.setItem('cust_phone', urlPhone);
    if (urlToken) {
      localStorage.setItem('cust_token', urlToken);
      // Limpa a URL sem recarregar
      window.history.replaceState({}, '', window.location.pathname);
    }
    return loadCustomer();
  });
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'upcoming' | 'past' | 'profile'>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'profile' ? 'profile' : 'upcoming';
  });
  // Busca a company só pra ler a flag de UI e o tema — MyBookingsPage nunca
  // buscava isso, porque antes só recebia "company" embutida dentro de cada
  // booking (e um cliente sem agendamentos não teria nenhum).
  //
  // HOTFIX (flash de cor): antes, MyAccountV2 montava IMEDIATAMENTE com o
  // fallback dourado (pilotCompany ainda null) e só re-pintava com a cor real
  // quando o GET terminava — um frame visível com a cor errada. Agora:
  // - pilotCompanyLoaded distingue "carregando" de "carregado sem brand_color";
  // - cache por slug em sessionStorage (nunca cruza slugs — chave é o próprio
  //   slug) deixa revisitas na mesma aba instantâneas, sem skeleton nem flash;
  // - o slug da requisição é comparado a getSlug() no retorno, ignorando
  //   respostas atrasadas se o slug tiver mudado nesse meio-tempo.
  const readCachedTheme = (slug: string): any => {
    try {
      const raw = sessionStorage.getItem('pilot_theme_' + slug);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const initialSlugRef = useRef(getSlug());
  const [pilotCompany, setPilotCompany] = useState<any>(() => {
    const cached = readCachedTheme(initialSlugRef.current);
    return cached ? { settings: cached } : null;
  });
  const [pilotCompanyLoaded, setPilotCompanyLoaded] = useState<boolean>(() => !!readCachedTheme(initialSlugRef.current));
  useEffect(() => {
    const slugAtual = getSlug();
    axios.get(`${API}/public/${slugAtual}`).then(({ data }) => {
      if (getSlug() !== slugAtual) return; // ignora resposta atrasada de um slug que não é mais o atual
      setPilotCompany(data);
      try {
        sessionStorage.setItem('pilot_theme_' + slugAtual, JSON.stringify({
          brand_color: data?.settings?.brand_color || null,
          public_dark_mode: data?.settings?.public_dark_mode === true,
          legacy_client_ui: data?.settings?.legacy_client_ui === true,
        }));
      } catch {}
    }).catch(() => {}).finally(() => {
      if (getSlug() === slugAtual) setPilotCompanyLoaded(true);
    });
  }, []);
  // UI nova (MyAccountV2) é o padrão GLOBAL desde a liberação de 05/08/2026.
  // Fallback individual: settings.legacy_client_ui=true volta uma empresa
  // específica à UI antiga sem afetar as demais.
  const pilotUI = pilotCompany?.settings?.legacy_client_ui !== true;
  // Cor principal e modo escuro vêm da MESMA fonte da página pública (BookingPage):
  // company.settings.brand_color e public_dark_mode (ou ?preview_dark=1).
  const pilotAccent = pilotCompany?.settings?.brand_color || '#c9a227';
  const pilotDark = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview_dark') === '1')
    || pilotCompany?.settings?.public_dark_mode === true;
  const [error, setError] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState('');
  const [reschedTime, setReschedTime] = useState('');
  const [reschedSlots, setReschedSlots] = useState<any[]>([]);
  const [reschedLoading, setReschedLoading] = useState(false);

  // Review states
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewBooking, setReviewBooking] = useState<any>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBooking, setDetailBooking] = useState<any>(null);
  
  // Profile states
  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileBirthday, setProfileBirthday] = useState(''); // yyyy-MM-dd (ISO)
  const [birthdayDisplay, setBirthdayDisplay] = useState(''); // dd/mm/aaaa (visual)
  const [profileAddress, setProfileAddress] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [profilePwd, setProfilePwd] = useState('');
  const [profilePwd2, setProfilePwd2] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [editingPhone, setEditingPhone] = useState(false);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const photoRef = useRef<any>(null);

  useEffect(() => {
    // Se não tem custUser mas tem token (ex: cross-domain redirect), tenta buscar do servidor
    if (!custUser) {
      const orphanToken = localStorage.getItem('cust_token');
      const orphanSlug = localStorage.getItem('cust_slug') || 'demo';
      const urlPhone = getUrlPhone();
      const savedPhone = cleanPhone(localStorage.getItem('cust_phone'));
      const phoneToUse = urlPhone || savedPhone;

      if (orphanToken && orphanToken !== 'null' && orphanToken !== 'undefined') {
        axios.get(`${API}/public/${orphanSlug}/customers/me`, {
          headers: { Authorization: `Bearer ${orphanToken}` }
        }).then(({ data }) => {
          const user = { id: data.id, name: data.name, phone: data.phone, email: data.email, photo_url: data.photo_url, birthday: data.birthday };
          localStorage.setItem('cust_user', JSON.stringify(user));
          if (data.phone) localStorage.setItem('cust_phone', cleanPhone(data.phone));
          if (data.fresh_token) localStorage.setItem('cust_token', data.fresh_token);
          setCustUser(user);
        }).catch(() => setLoading(false));
      } else if (phoneToUse) {
        axios.get(`${API}/public/${orphanSlug}/customers/find-by-phone`, {
          params: { phone: phoneToUse }
        }).then(({ data }) => {
          const customer = data.customer || data;
          const token = data.token || data.customer_token || data.access_token || data.jwt;
          const user = {
            id: customer.id,
            name: customer.name,
            phone: customer.phone || phoneToUse,
            email: customer.email,
            photo_url: customer.photo_url,
            birthday: customer.birthday
          };
          localStorage.setItem('cust_user', JSON.stringify(user));
          localStorage.setItem('cust_phone', cleanPhone(user.phone || phoneToUse));
          if (token) localStorage.setItem('cust_token', token);
          setCustUser(user);
          window.history.replaceState({}, '', window.location.pathname);
        }).catch(() => {
          setTokenExpired(true);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
      return;
    }
    setProfileName(custUser.name || '');
    // Usa dados do localStorage como valor inicial (já decriptados quando foram salvos)
    const cachedPhone = custUser.phone && !custUser.phone.startsWith('U2Fsd') ? custUser.phone : '';
    const cachedEmail = custUser.email && !custUser.email.startsWith('U2Fsd') && custUser.email.includes('@') ? custUser.email : '';
    setProfilePhone(cachedPhone);
    setProfileEmail(cachedEmail);
    const bday = custUser.birthday ? custUser.birthday.split('T')[0] : '';
    setProfileBirthday(bday);
    setBirthdayDisplay(bday ? bday.split('-').reverse().join('/') : '');
    setProfileAddress(custUser.address || '');
    setProfilePhoto(custUser.photo_url || '');

    const token = localStorage.getItem('cust_token');

    // Token ausente ou inválido: não tenta nada, mostra botão de renovar
    if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
      setTokenExpired(true);
      setLoading(false);
      return;
    }

    // Função para carregar bookings com um token
    const fetchBookings = (authToken: string) => {
      axios.get(`${API}/public/${getSlug()}/customers/bookings`, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then(({ data }) => {
          const bookingsList = Array.isArray(data) ? data : (data.bookings || []);
          setBookings(bookingsList);
        })
        .catch(e => {
          const status = e.response?.status;
          const errMsg = e.response?.data?.error || '';
          if (status === 401) {
            setTokenExpired(true); // Token inválido: mostra botão de renovar acesso
          } else {
            setError(errMsg || 'Erro ao carregar agendamentos.');
          }
        })
        .finally(() => setLoading(false));
    };

    // Primeiro atualiza perfil e renova token, depois carrega bookings
    axios.get(`${API}/public/${getSlug()}/customers/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(({ data }) => {
      if (data.phone && !data.phone.startsWith('U2Fsd')) setProfilePhone(data.phone);
      if (data.email && data.email.includes('@')) setProfileEmail(data.email);
      const freshToken = data.fresh_token || token;
      if (data.fresh_token) localStorage.setItem('cust_token', data.fresh_token);
      saveCustomer({ ...loadCustomer(), phone: data.phone || cachedPhone, email: data.email || cachedEmail });
      setTokenExpired(false);
      fetchBookings(freshToken!);
    }).catch(e => {
      console.warn('[me] erro:', e.response?.status, e.response?.data?.error);
      if (e.response?.status === 401) {
        // Token completamente inválido: não consegue nem /me
        setTokenExpired(true);
        setLoading(false);
      } else {
        // Erro de rede etc: tenta bookings mesmo assim
        fetchBookings(token || '');
      }
    });
  }, [custUser?.id]);

  useEffect(() => {
    const token = localStorage.getItem('cust_token');
    if (!custUser || !token || token === 'null' || token === 'undefined') {
      setSubscriptions([]);
      setSubscriptionsLoading(false);
      return;
    }

    setSubscriptionsLoading(true);
    axios.get(`${API}/public/${getSlug()}/customers/subscriptions`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : (data?.subscriptions || []);
        setSubscriptions(rows);
      })
      .catch(() => setSubscriptions([]))
      .finally(() => setSubscriptionsLoading(false));
  }, [custUser?.id]);

  const handleCancel = async () => {
    if (!selectedBooking?.token) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/public/bookings/${selectedBooking.token}/cancel`, { reason: cancelReason });
      setBookings(bs => bs.map(b => b.id === selectedBooking.id ? { ...b, status: 'cancelled' } : b));
      setCancelOpen(false);
      setCancelReason('');
      setSelectedBooking(null);
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || 'Tente novamente.'));
    } finally { setProcessing(false); }
  };

  const getThemeColor = (b: any) => b?.company?.brand_color || '#1a1a1a';

  const openReschedule = (b: any) => {
    setSelectedBooking(b);
    setReschedDate('');
    setReschedTime('');
    setReschedSlots([]);
    setRescheduleOpen(true);
  };

  const loadSlots = async (dateStr: string) => {
    if (!selectedBooking) return;
    setReschedLoading(true);
    setReschedTime('');
    try {
      const { data } = await axios.get(`${API}/public/${selectedBooking.company?.slug || getSlug()}/availability`, {
        params: { date: dateStr, service_id: selectedBooking.service_id, professional_id: selectedBooking.professional_id }
      });
      setReschedSlots(data.slots || []);
    } catch { setReschedSlots([]); }
    finally { setReschedLoading(false); }
  };

  const handleReschedule = async () => {
    if (!selectedBooking?.token || !reschedTime) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/public/bookings/${selectedBooking.token}/reschedule`, {
        service_id: selectedBooking.service_id,
        professional_id: selectedBooking.professional_id,
        date: reschedDate,
        time: reschedTime
      });
      setBookings(bs => bs.map(b => b.id === selectedBooking.id ? { ...b, date: reschedDate, start_time: reschedTime, status: 'confirmed' } : b));
      setRescheduleOpen(false);
      setSelectedBooking(null);
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || 'Tente novamente.'));
    } finally { setProcessing(false); }
  };

  const uploadPhoto = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const custTokenUpload = localStorage.getItem('cust_token');
    const { data } = await axios.post(`${API}/upload/customer-photo`, form, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(custTokenUpload ? { Authorization: `Bearer ${custTokenUpload}` } : {})
      }
    });
    return data.url;
  };

  const handlePhotoChange = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfileSaving(true);
    try {
      const url = await uploadPhoto(file);
      const token = localStorage.getItem('cust_token');
      const { data: saved } = await axios.put(`${API}/public/${getSlug()}/customers/me`, {
        name: profileName || custUser?.name,
        photo_url: url
      }, { headers: { Authorization: `Bearer ${token}` } });
      const c = saved.customer || {};
      const updated = { ...custUser, photo_url: url, name: c.name || profileName || custUser?.name };
      saveCustomer(updated);
      setCustUser(updated);
      setProfilePhoto(url);
      setProfileSuccess('Foto atualizada!');
      setTimeout(() => setProfileSuccess(''), 2500);
    } catch(e) {
      setProfileError('Erro ao salvar a foto. Tente novamente.');
      setTimeout(() => setProfileError(''), 3000);
    }
    finally { setProfileSaving(false); }
  };

  const handleSaveProfile = async () => {
    if (!profileName.trim()) {
      setProfileError('Nome é obrigatório.');
      return;
    }
    if (profilePwd && profilePwd !== profilePwd2) {
      setProfileError('As senhas não coincidem.');
      return;
    }
    const cleanPhone = profilePhone.replace(/\D/g, '');
    if (cleanPhone && (cleanPhone.length < 10 || cleanPhone.length > 11)) {
      setProfileError('Telefone inválido. Use DDD + número (10 ou 11 dígitos).');
      return;
    }
    if (profileEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileEmail.trim())) {
      setProfileError('E-mail inválido.');
      return;
    }
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      const token = localStorage.getItem('cust_token');
      const body: any = { name: profileName, photo_url: profilePhoto };
      if (profileEmail) body.email = profileEmail;
      if (profilePhone) body.phone = profilePhone.replace(/\D/g, '');
      if (profileBirthday) body.birthday = profileBirthday;
      if (profileAddress) body.address = profileAddress;
      if (profilePwd) body.password = profilePwd;
      
      const { data: saved } = await axios.put(`${API}/public/${getSlug()}/customers/me`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // O endpoint PUT /me retorna os dados diretamente (não dentro de .customer)
      const c = saved.id ? saved : (saved.customer || {});
      const updated = { ...custUser, name: c.name || profileName, email: c.email !== undefined ? c.email : profileEmail, phone: c.phone !== undefined ? c.phone : profilePhone, birthday: c.birthday || profileBirthday, photo_url: c.photo_url || profilePhoto };
      if (c.fresh_token) localStorage.setItem('cust_token', c.fresh_token);
      saveCustomer(updated);
      setCustUser(updated);
      if (c.phone !== undefined && !String(c.phone || '').startsWith('U2Fsd')) setProfilePhone(c.phone || '');
      if (c.email !== undefined && String(c.email || '').includes('@')) setProfileEmail(c.email || '');
      setProfileSuccess('Perfil atualizado com sucesso!');
      setProfilePwd('');
      setProfilePwd2('');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (e: any) {
      console.error('Save error:', e);
      setProfileError(e.response?.data?.error || 'Erro ao salvar.');
    } finally { setProfileSaving(false); }
  };

  const upcoming = bookings.filter(b => {
    const d = parseISO(b.date + 'T12:00:00');
    return !isBefore(d, today) && !['cancelled', 'completed', 'no_show'].includes(b.status);
  });
  const past = bookings.filter(b => {
    const d = parseISO(b.date + 'T12:00:00');
    return isBefore(d, today) || ['cancelled', 'completed', 'no_show'].includes(b.status);
  });
  const list = tab === 'upcoming' ? upcoming : past;
  // Piloto: ordena por data+horário (a API não garante ordem). Próximos crescente
  // (o primeiro é o "próximo horário"); histórico decrescente (recente primeiro).
  // Só o piloto usa estas versões ordenadas — a UI antiga segue inalterada.
  const upcomingSorted = sortUpcomingAsc(upcoming);
  const pastSorted = sortPastDesc(past);
  const listSorted = tab === 'upcoming' ? upcomingSorted : pastSorted;
  // Adendo 5.4.3 — Item 2: "pacote que vence primeiro aparece primeiro" já
  // vem garantido pelo backend (GET .../customers/subscriptions ordena
  // end_date ASC) — sem reordenar de novo aqui.
  const activeSubscriptions = subscriptions.filter((row: any) => row.status === 'active');

  // Piloto: pequenos adaptadores pra MyAccountV2 chamar exatamente as MESMAS
  // ações/estado que a versão atual já usa — nenhuma lógica nova aqui.
  const isHistoricalForCard = (b: any) => {
    const d = parseISO(b.date + 'T12:00:00');
    return isBefore(d, today) || ['cancelled', 'completed', 'no_show'].includes(b.status);
  };
  const openCancel = (b: any) => { setSelectedBooking(b); setCancelOpen(true); };
  const openReview = (b: any) => { setReviewBooking(b); setReviewRating(b.review?.rating || 5); setReviewComment(b.review?.comment || ''); setReviewOpen(true); };
  const openDetailV2 = (b: any) => { setDetailBooking(b); setDetailOpen(true); };
  // Repetir: reaproveita serviço + profissional do agendamento histórico e abre
  // direto a escolha de data/horário na página pública (fluxo normal de criação).
  // Cria um NOVO agendamento; nunca altera o histórico. Serviço/profissional
  // inativo é tratado na BookingPage (avisa e deixa escolher outro).
  const onRepeat = (b: any) => {
    const sl = b.company?.slug || getSlug();
    const svcId = b.service?.id || b.booking_services?.[0]?.service?.id;
    const pid = b.professional?.id;
    const q = new URLSearchParams();
    q.set('repeat', '1');
    if (svcId) q.set('service', String(svcId));
    if (pid) q.set('professional', String(pid));
    window.location.href = '/book/' + sl + '?' + q.toString();
  };
  const onBookNew = () => { window.location.href = window.location.origin + '/book/' + getSlug(); };
  // Adendo 5.4.3 — Item 2: "Agendar com meu saldo" — mesmo destino que o
  // botão morto da versão antiga já usava (tab=subscriptions na vitrine).
  const onUseSaldo = () => { window.location.href = window.location.origin + '/book/' + getSlug() + '?tab=subscriptions'; };
  const onLogout = () => { localStorage.removeItem('cust_token'); localStorage.removeItem('cust_user'); window.location.reload(); };

  if (!custUser) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', maxWidth: 360 }}>
          <Person sx={{ fontSize: 48, color: '#bbb', mb: 2 }} />
          <Typography fontWeight={700} fontSize={18} mb={1}>Faça login para continuar</Typography>
          <Button variant="contained" onClick={() => window.history.back()}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, borderRadius: 2, textTransform: 'none', fontWeight: 700, mt: 2 }}>
            Voltar
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {pilotUI ? (
        !pilotCompanyLoaded ? (
          // HOTFIX: enquanto a empresa ainda não carregou, mostra um skeleton
          // neutro — nunca o MyAccountV2 com o fallback dourado ou com a cor
          // de uma empresa anterior (era essa a origem do flash reportado).
          <MyAccountSkeleton />
        ) : (
        <MyAccountV2
          custUser={custUser} tab={tab} setTab={setTab}
          upcoming={upcomingSorted} past={pastSorted} list={listSorted} isHistorical={isHistoricalForCard}
          loading={loading} error={error} tokenExpired={tokenExpired}
          activeSubscriptions={activeSubscriptions} subscriptionsLoading={subscriptionsLoading}
          openReschedule={openReschedule} openCancel={openCancel} openReview={openReview}
          openDetail={openDetailV2} onRepeat={onRepeat} onBookNew={onBookNew} onUseSaldo={onUseSaldo}
          profileName={profileName} setProfileName={setProfileName}
          profilePhone={profilePhone} setProfilePhone={setProfilePhone}
          profileEmail={profileEmail} setProfileEmail={setProfileEmail}
          profileBirthday={profileBirthday} setProfileBirthday={setProfileBirthday}
          profileAddress={profileAddress} setProfileAddress={setProfileAddress}
          profilePhoto={profilePhoto}
          profilePwd={profilePwd} setProfilePwd={setProfilePwd}
          profilePwd2={profilePwd2} setProfilePwd2={setProfilePwd2}
          profileSaving={profileSaving} profileSuccess={profileSuccess} profileError={profileError}
          handleSaveProfile={handleSaveProfile} handlePhotoChange={handlePhotoChange}
          onLogout={onLogout}
          accentColor={pilotAccent}
          isDark={pilotDark}
        />
        )
      ) : (
      <>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #eee', px: 2, py: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>
        <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton size="small" onClick={() => window.history.back()} sx={{ color: '#444' }}>
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Typography fontWeight={800} fontSize={17}>Minha Conta</Typography>
            <Typography variant="caption" color="text.secondary">{custUser.name}</Typography>
          </Box>
          <Box sx={{ cursor: 'pointer' }} onClick={() => setTab('profile')}>
            <Avatar src={custUser.photo_url} sx={{ width: 38, height: 38, bgcolor: '#1a1a1a', fontSize: 16, fontWeight: 700, border: tab === 'profile' ? '2px solid #1a1a1a' : '2px solid transparent' }}>
              {custUser.name?.[0]}
            </Avatar>
          </Box>
        </Box>
      </Box>

      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
        <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex' }}>
          {[
            { key: 'upcoming', label: `Próximos (${upcoming.length})` },
            { key: 'past', label: `Histórico (${past.length})` },
            { key: 'profile', label: 'Meu Perfil' },
          ].map(t => (
            <Box key={t.key} onClick={() => setTab(t.key as any)}
              sx={{ flex: 1, py: 1.8, textAlign: 'center', cursor: 'pointer',
                borderBottom: tab === t.key ? '3px solid #1a1a1a' : '3px solid transparent',
                bgcolor: tab === t.key ? 'rgba(0,0,0,0.04)' : 'transparent',
                transition: 'all 0.15s' }}>
              <Typography fontWeight={tab === t.key ? 800 : 500} fontSize={13} color={tab === t.key ? '#1a1a1a' : '#999'}>
                {t.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 560, mx: 'auto', p: 2 }}>
        {(tab === 'upcoming' || tab === 'past') && (
          subscriptionsLoading ? (
            <Paper sx={{ mb: 2, borderRadius: 3, border: '1px solid #ebebeb', p: 2.5, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <Box display="flex" justifyContent="center" py={1.5}>
                <CircularProgress size={24} />
              </Box>
            </Paper>
          ) : activeSubscriptions.length > 0 ? (
            <Paper sx={{ mb: 2, borderRadius: 3, border: '1px solid #dbeafe', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <Box sx={{ px: 2.5, py: 1.7, borderBottom: '1px solid #eff6ff', bgcolor: '#f8fbff' }}>
                <Typography fontWeight={900} fontSize={15} color="#1a1a1a">Seu saldo em pacotes</Typography>
                <Typography fontSize={12.5} color="#64748b" mt={0.4}>
                  Use seu pacote por aqui. Escolha o saldo restante e reagende sem misturar com serviços avulsos.
                </Typography>
              </Box>

              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {activeSubscriptions.map((row: any) => {
                  const remainingItems = Array.isArray(row.items) ? row.items.filter((item: any) => getRemainingSubscriptionQty(item) > 0) : [];
                  const daysLeft = getSubscriptionDaysLeft(row.end_date);
                  return (
                    <Box key={row.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2.5, p: 1.6, bgcolor: '#fff' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.8 }}>
                        <Box>
                          <Typography fontWeight={800} fontSize={14} color="#1a1a1a">
                            {row.subscription_plan?.public_title || row.plan_name}
                          </Typography>
                          <Typography fontSize={12} color="#64748b" mt={0.25}>
                            {daysLeft > 0 ? `${daysLeft} dia(s) restante(s)` : 'Último dia de uso'}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${remainingItems.length} item(ns) com saldo`}
                          size="small"
                          sx={{ bgcolor: '#e0ecff', color: '#2563eb', fontWeight: 800, height: 22, fontSize: 10.5 }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, mb: 1.2 }}>
                        {remainingItems.slice(0, 4).map((item: any) => (
                          <Typography key={item.id} fontSize={12.4} color="#475569">
                            <Box component="span" sx={{ fontWeight: 900, color: '#111827' }}>
                              {getRemainingSubscriptionQty(item)}x
                            </Box>
                            {' '}
                            {item.service?.name || item.service_name}
                          </Typography>
                        ))}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => { window.location.href = window.location.origin + '/book/' + getSlug() + '?tab=subscriptions'; }}
                        sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, borderRadius: 2.5, textTransform: 'none', fontWeight: 800 }}
                      >
                        Usar saldo agora
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          ) : null
        )}

        {tab === 'profile' && (
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, pb: 2 }}>
              <Box sx={{ position: 'relative', mb: 1.5 }}>
                <Avatar src={profilePhoto || custUser?.photo_url} sx={{ width: 88, height: 88, fontSize: 32, fontWeight: 700, bgcolor: '#1a1a1a', border: '3px solid #fff', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                  {(profileName || custUser?.name || 'C')[0].toUpperCase()}
                </Avatar>
                <Box
                  component="label"
                  sx={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, borderRadius: '50%', bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', '&:hover': { bgcolor: '#333' } }}
                >
                  <CameraAlt sx={{ fontSize: 14, color: '#fff' }} />
                  <input type="file" accept="image/*" hidden onChange={handlePhotoChange} />
                </Box>
              </Box>
              <Typography fontWeight={800} fontSize={17} color="#1a1a1a">{profileName || custUser?.name}</Typography>
              <Typography fontSize={12} color="#999" mt={0.3}>{profilePhone || custUser?.phone}</Typography>
            </Box>

            {profileSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{profileSuccess}</Alert>}
            {profileError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{profileError}</Alert>}

            <Paper sx={{ borderRadius: 3, border: '1px solid #ebebeb', overflow: 'hidden', mb: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0', bgcolor: '#fafafa' }}>
                <Typography fontWeight={800} fontSize={13} color="#1a1a1a" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Dados Pessoais</Typography>
              </Box>
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Nome completo" value={profileName} onChange={e => setProfileName(e.target.value)} fullWidth size="small" InputProps={{ sx: { borderRadius: 2 } }} />
                <TextField label="WhatsApp" value={profilePhone} onChange={e => setProfilePhone(e.target.value)} fullWidth size="small" placeholder="(11) 99999-9999" InputProps={{ sx: { borderRadius: 2 } }} />
                <TextField label="E-mail" value={profileEmail} onChange={e => setProfileEmail(e.target.value)} fullWidth size="small" type="email" InputProps={{ sx: { borderRadius: 2 } }} />
                <TextField label="Data de nascimento" value={profileBirthday} onChange={e => setProfileBirthday(e.target.value)} fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: 2 } }} />
                <TextField label="Endereco" value={profileAddress} onChange={e => setProfileAddress(e.target.value)} fullWidth size="small" placeholder="Rua, numero, bairro" InputProps={{ sx: { borderRadius: 2 } }} />
              </Box>
            </Paper>

            <Paper sx={{ borderRadius: 3, border: '1px solid #ebebeb', overflow: 'hidden', mb: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0', bgcolor: '#fafafa' }}>
                <Typography fontWeight={800} fontSize={13} color="#1a1a1a" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Alterar Senha</Typography>
              </Box>
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Nova senha" value={profilePwd} onChange={e => setProfilePwd(e.target.value)} fullWidth size="small" type="password" placeholder="Minimo 6 caracteres" InputProps={{ sx: { borderRadius: 2 } }} />
                <TextField label="Confirmar nova senha" value={profilePwd2} onChange={e => setProfilePwd2(e.target.value)} fullWidth size="small" type="password" InputProps={{ sx: { borderRadius: 2 } }} />
              </Box>
            </Paper>

            <Button variant="contained" fullWidth disabled={profileSaving} onClick={handleSaveProfile} sx={{ bgcolor: '#1a1a1a', color: '#fff', borderRadius: 3, textTransform: 'none', fontWeight: 800, py: 1.5, fontSize: 15, mb: 2, '&:hover': { bgcolor: '#333' } }}>
              {profileSaving ? 'Salvando...' : 'Salvar alteracoes'}
            </Button>

            <Button fullWidth variant="outlined" onClick={() => { localStorage.removeItem('cust_token'); localStorage.removeItem('cust_user'); window.location.reload(); }} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, py: 1.2, fontSize: 14, color: '#e53e3e', borderColor: '#fecaca', '&:hover': { bgcolor: '#fff5f5', borderColor: '#e53e3e' } }}>
              Sair da conta
            </Button>
            <Box sx={{ height: 24 }} />
          </Box>
        )}


        {(tab === 'upcoming' || tab === 'past') && (
          <>
            {loading ? <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box> : error ? <Alert severity="error">{error}</Alert> : tokenExpired ? <Box py={6} textAlign="center" px={3}><CalendarMonth sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} /><Typography fontWeight={700} fontSize={15} mb={1}>Sessão expirada</Typography><Typography color="text.secondary" fontSize={13} mb={3}>Informe seu WhatsApp para ver seus agendamentos</Typography><Box sx={{ maxWidth: 320, mx: 'auto', display: 'flex', gap: 1 }}><TextField size="small" fullWidth placeholder="(11) 99999-9999" inputProps={{ maxLength: 15 }} id="reauth-phone" /><Button variant="contained" onClick={async () => { const v = (document.getElementById('reauth-phone') as any)?.value || ''; const d = v.replace(/\D/g,''); const c = d.length === 10 ? d.slice(0,2)+'9'+d.slice(2) : d; if (c.length < 10) return; const slug = getSlug(); try { const { data } = await axios.get(`${API}/public/${slug}/customers/find-by-phone?phone=${c}`); if (data?.token) { localStorage.setItem('cust_token', data.token); if (data.customer) localStorage.setItem('cust_user', JSON.stringify(data.customer)); window.location.reload(); } } catch { window.location.href = window.location.origin; } }} sx={{ bgcolor: '#1a1a1a', color: '#fff', borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 2, whiteSpace:'nowrap' }}>Ver agenda</Button></Box><Button variant="text" onClick={() => { window.location.href = window.location.origin; }} sx={{ mt: 2, color: '#888', textTransform: 'none', fontSize: 12 }}>Ou faça um novo agendamento</Button></Box> : list.length === 0 ? <Box py={6} textAlign="center"><CalendarMonth sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} /><Typography color="text.secondary">{tab === 'upcoming' ? 'Nenhum agendamento futuro' : 'Nenhum histórico'}</Typography></Box> : list.map((b) => {
              const status = STATUS_MAP[b.status] || STATUS_MAP.pending;
              const dateObj = parseISO(b.date + 'T12:00:00');
              const isHistorical = isBefore(dateObj, today) || ['cancelled', 'completed', 'no_show'].includes(b.status);
              return (
                <Paper key={b.id} sx={{ mb: 2, borderRadius: 3, border: '1px solid #ebebeb', overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
                  {isHistorical && b.review && (<Box sx={{ px: 2.5, pt: 1.5, pb: 0.3, display: "flex", alignItems: "center", justifyContent: "space-between" }}><Box sx={{ display: "flex", alignItems: "center", gap: 0.4, cursor: "pointer" }} onClick={() => { setDetailBooking(b); setDetailOpen(true); }}><Star sx={{ fontSize: 15, color: "#f59e0b" }} /><Typography fontSize={13} color="#f59e0b" fontWeight={700}>Voce deu Nota {b.review.rating}</Typography></Box><Typography fontSize={12} color="#1976d2" fontWeight={700} sx={{ cursor: "pointer" }} onClick={() => { setReviewBooking(b); setReviewRating(b.review.rating); setReviewComment(b.review.comment || ""); setReviewOpen(true); }}>Editar</Typography></Box>)}
                  {/* Avaliar atendimento concluído ainda sem avaliação (some assim que o cliente avalia) */}
                  {b.status === 'completed' && !b.review && (<Box onClick={() => { setReviewBooking(b); setReviewRating(5); setReviewComment(''); setReviewOpen(true); }} sx={{ px: 2.5, pt: 1.5, pb: 0.3, display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}><StarBorder sx={{ fontSize: 16, color: "#f59e0b" }} /><Typography fontSize={13} color="#f59e0b" fontWeight={700}>Avaliar atendimento</Typography></Box>)}
                  <Box sx={{ px: 2.5, py: 2, bgcolor: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography fontWeight={800} fontSize={14} color="#1a1a1a">{b.company?.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.2 }}>
                        <CalendarMonth sx={{ fontSize: 12, color: '#888' }} />
                        <Typography variant="caption" color="text.secondary">{format(dateObj, "EEE, dd 'de' MMMM", { locale: ptBR })} às {b.start_time?.slice(0, 5)}</Typography>
                      </Box>
                    </Box>
                    <Chip label={status.label} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: 11, px: 0.5 }} />
                  </Box>
                  <Box sx={{ px: 2.5, py: 2 }}>
                    {b.booking_services && b.booking_services.length > 0 ? (
                      // Multi-serviço
                      <Box>
                        {b.booking_services.map((bs: any, idx: number) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: idx < b.booking_services.length - 1 ? 1 : 0 }}>
                            <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                              {bs.service?.image_url ? <img src={bs.service.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ContentCut sx={{ fontSize: 20, color: '#ccc' }} />}
                            </Box>
                            <Box flex={1}>
                              <Typography fontWeight={700} fontSize={14}>{bs.service?.name}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTime sx={{ fontSize: 12, color: '#bbb' }} />
                                <Typography variant="caption" color="text.secondary">{bs.service?.duration_min}min {b.professional?.name && `· ${b.professional.name}`}</Typography>
                              </Box>
                            </Box>
                            <Typography fontWeight={800} fontSize={15} color="#1a1a1a">R$ {Number(bs.price || 0).toFixed(2).replace('.', ',')}</Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      // Singular (compatibilidade)
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                          {b.service?.image_url ? <img src={b.service.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ContentCut sx={{ fontSize: 20, color: '#ccc' }} />}
                        </Box>
                        <Box flex={1}>
                          <Typography fontWeight={700} fontSize={14}>{b.service?.name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime sx={{ fontSize: 12, color: '#bbb' }} />
                            <Typography variant="caption" color="text.secondary">{b.service?.duration_min}min {b.professional?.name && `· ${b.professional.name}`}</Typography>
                          </Box>
                        </Box>
                        <Typography fontWeight={800} fontSize={15} color="#1a1a1a">R$ {Number(b.price || b.service?.price || 0).toFixed(2).replace('.', ',')}</Typography>
                      </Box>
                    )}
                    {/* Rodapé */}
                    {isHistorical ? (
                      <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 1.2, mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" startIcon={<Refresh sx={{ fontSize: 12 }} />}
                          onClick={() => { const sl = b.company?.slug || getSlug(); const pid = b.professional?.id; window.location.href = '/book/' + sl + (pid ? '?professional=' + pid : ''); }}
                          sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, color: '#555', px: 1 }}>
                          Repetir
                        </Button>
                      </Box>
                    ) : b.company?.allow_customer_cancel !== false ? (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                        <Button size="small" startIcon={<Edit sx={{ fontSize: 14 }} />} onClick={() => openReschedule(b)} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: '#1976d2', color: '#fff', borderRadius: 2, px: 2, py: 0.7, '&:hover': { bgcolor: '#1565c0' } }}>Reagendar</Button>
                        <Button size="small" startIcon={<Close sx={{ fontSize: 14 }} />} onClick={() => { setSelectedBooking(b); setCancelOpen(true); }} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: '#ef5350', color: '#fff', borderRadius: 2, px: 2, py: 0.7, '&:hover': { bgcolor: '#e53935' } }}>Cancelar</Button>
                      </Box>
                    ) : null}
                  </Box>
                </Paper>
              );
            })}
          </>
        )}
      </Box>
      </>
      )}

      {/* Modal detalhes booking — compartilhado entre visual antigo e piloto */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {detailBooking && (
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Typography fontWeight={900} fontSize={19}>{format(parseISO(detailBooking.date + "T12:00:00"), "dd MMM yyyy", { locale: ptBR })} as {detailBooking.start_time?.slice(0,5)}</Typography>
              <Chip label={STATUS_MAP[detailBooking.status]?.label || ""} size="small" sx={{ bgcolor: STATUS_MAP[detailBooking.status]?.bg, color: STATUS_MAP[detailBooking.status]?.color, fontWeight: 700, fontSize: 11 }} />
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
              <Box sx={{ fontSize: 22 }}>🏪</Box>
              <Box><Typography fontWeight={700} fontSize={14}>{detailBooking.company?.name}</Typography></Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
              <Box sx={{ fontSize: 22 }}>📅</Box>
              <Box><Typography fontWeight={700} fontSize={14}>{detailBooking.service?.name}</Typography><Typography fontSize={12} color="text.secondary">Profissional: {detailBooking.professional?.name}</Typography></Box>
            </Box>
            {detailBooking.review && <Box sx={{ display: "flex", gap: 0.4, mb: 2, alignItems: "center" }}>{[1,2,3,4,5].map((s: number) => (<Star key={s} sx={{ fontSize: 20, color: s <= detailBooking.review.rating ? "#f59e0b" : "#e0e0e0" }} />))}<Typography fontSize={13} color="#f59e0b" fontWeight={700} ml={0.5}>Nota {detailBooking.review.rating}</Typography></Box>}
            <Box sx={{ textAlign: "right", mt: 1 }}><Button onClick={() => setDetailOpen(false)} sx={{ textTransform: "none", fontWeight: 700 }}>Fechar</Button></Box>
          </DialogContent>
        )}
      </Dialog>

      {/* Dialog de Avaliação */}
      <Dialog open={reviewOpen} onClose={() => !reviewSaving && setReviewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{reviewBooking?.review?.id ? 'Editar avaliação' : 'Avaliar atendimento'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 2, mt: 1 }}>
            {[1,2,3,4,5].map(s => (
              <IconButton key={s} size="small" onClick={() => setReviewRating(s)}>
                {s <= reviewRating ? <Star sx={{ color: '#f59e0b', fontSize: 28 }} /> : <StarBorder sx={{ color: '#f59e0b', fontSize: 28 }} />}
              </IconButton>
            ))}
          </Box>
          <TextField fullWidth multiline rows={3} placeholder="Conte como foi sua experiência (opcional)" value={reviewComment} onChange={e => setReviewComment(e.target.value)} size="small" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReviewOpen(false)} disabled={reviewSaving} sx={{ textTransform: 'none', color: '#666' }}>Cancelar</Button>
          <Button variant="contained" disabled={reviewSaving} onClick={async () => {
            if (!reviewBooking) return;
            setReviewSaving(true);
            try {
              const token = localStorage.getItem('cust_token') || '';
              const headers = { headers: { Authorization: 'Bearer ' + token } };
              if (reviewBooking.review?.id) {
                // Editar avaliação existente
                await axios.put(API + '/public/reviews/' + reviewBooking.review.id, { rating: reviewRating, comment: reviewComment }, headers);
                setBookings(prev => prev.map(b => b.id === reviewBooking.id ? { ...b, review: { ...b.review, rating: reviewRating, comment: reviewComment } } : b));
              } else {
                // Criar avaliação para este atendimento concluído
                const { data } = await axios.post(API + '/public/' + getSlug() + '/reviews', { rating: reviewRating, comment: reviewComment, booking_id: reviewBooking.id }, headers);
                setBookings(prev => prev.map(b => b.id === reviewBooking.id ? { ...b, review: { id: data.id, rating: reviewRating, comment: reviewComment, status: data.status } } : b));
              }
              setReviewOpen(false);
            } catch { } finally { setReviewSaving(false); }
          }} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#1a1a1a' }}>
            {reviewSaving ? 'Salvando...' : 'Salvar avaliação'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => !processing && setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar Agendamento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>Tem certeza?</Typography>
          <TextField fullWidth multiline rows={3} label="Motivo (opcional)" value={cancelReason} onChange={e => setCancelReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Voltar</Button>
          <Button onClick={handleCancel} color="error" variant="contained">{processing ? <CircularProgress size={20} /> : 'Confirmar'}</Button>
        </DialogActions>
      </Dialog>

      {selectedBooking && (
        <Dialog open={rescheduleOpen} onClose={() => !processing && setRescheduleOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
          {/* Header gradiente */}
          <Box sx={{ background: 'linear-gradient(135deg, ' + getThemeColor(selectedBooking) + ' 0%, ' + getThemeColor(selectedBooking) + 'cc 100%)', px: 3, pt: 3, pb: 2.5 }}>
            <Typography fontWeight={800} fontSize={18} color="#fff" mb={0.5}>{selectedBooking.service?.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedBooking.professional?.photo_url
                ? <Box component="img" src={selectedBooking.professional.photo_url} sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }} />
                : <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Person sx={{ fontSize: 16, color: '#fff' }} /></Box>
              }
              <Typography fontSize={13} color="rgba(255,255,255,0.8)">{selectedBooking.professional?.name || 'Qualquer profissional'}</Typography>
            </Box>
            {/* Horário atual */}
            <Box sx={{ mt: 1.5, px: 1.5, py: 1, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <CalendarMonth sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }} />
              <Typography fontSize={12} color="rgba(255,255,255,0.7)">
                {format(parseISO(selectedBooking.date + 'T12:00:00'), "EEE, dd 'de' MMM", { locale: ptBR })} às {selectedBooking.start_time?.slice(0, 5)}
              </Typography>
            </Box>
          </Box>

          <DialogContent sx={{ px: 2.5, py: 2 }}>
            {/* Seleção de data — scroll horizontal */}
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>Nova data</Typography>
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', py: 1.5, mx: -0.5, px: 0.5, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#ddd', borderRadius: 2 } }}>
              {Array.from({ length: 21 }).map((_, i) => {
                const d = addDays(today, i + 1);
                const ds = format(d, 'yyyy-MM-dd');
                const isSelected = reschedDate === ds;
                return (
                  <Box key={ds} onClick={() => { setReschedDate(ds); loadSlots(ds); }} sx={{ minWidth: 52, height: 68, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: isSelected ? 'none' : '1.5px solid #e8e8e8', bgcolor: isSelected ? getThemeColor(selectedBooking) : '#fafafa', transition: 'all .15s', flexShrink: 0, boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.18)' : 'none' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: isSelected ? 'rgba(255,255,255,0.85)' : '#999', textTransform: 'uppercase' }}>{format(d, 'EEE', { locale: ptBR }).slice(0,3)}</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: isSelected ? '#fff' : '#1a1a1a', lineHeight: 1.1 }}>{format(d, 'd')}</Typography>
                    <Typography sx={{ fontSize: 10, color: isSelected ? 'rgba(255,255,255,0.6)' : '#bbb' }}>{format(d, 'MMM', { locale: ptBR })}</Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Horários */}
            {reschedDate && (
              <Box mt={1.5}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>Horários disponíveis</Typography>
                <Box mt={1}>
                  {reschedLoading ? (
                    <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} sx={{ color: '#1a1a1a' }} /></Box>
                  ) : reschedSlots.filter((s: any) => s.available).length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <CalendarMonth sx={{ fontSize: 36, color: '#ddd', mb: 1 }} />
                      <Typography fontSize={13} color="text.secondary">Nenhum horário disponível</Typography>
                      <Typography fontSize={12} color="text.secondary">Tente outra data</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                      {reschedSlots.filter((s: any) => s.available).map((slot: any) => {
                        const isSel = reschedTime === slot.time;
                        return (
                          <Box key={slot.time} onClick={() => setReschedTime(slot.time)} sx={{ py: 1, borderRadius: 2.5, textAlign: 'center', cursor: 'pointer', border: isSel ? 'none' : '1.5px solid #e8e8e8', bgcolor: isSel ? getThemeColor(selectedBooking) : '#fafafa', boxShadow: isSel ? '0 3px 10px rgba(0,0,0,0.15)' : 'none', transition: 'all .15s' }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: isSel ? '#fff' : '#1a1a1a' }}>{slot.time}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>

          <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', gap: 1.5 }}>
            <Button fullWidth onClick={() => setRescheduleOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 3, border: '1.5px solid #e8e8e8', color: '#555' }}>Voltar</Button>
            <Button fullWidth onClick={handleReschedule} disabled={!reschedTime || processing} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 3, bgcolor: getThemeColor(selectedBooking), color: '#fff', '&:hover': { bgcolor: getThemeColor(selectedBooking) + 'cc' }, '&:disabled': { bgcolor: '#e0e0e0', color: '#bbb' } }}>
              {processing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirmar'}
            </Button>
          </Box>
        </Dialog>
      )}
    </Box>
  );
}
